import React, { useState, useRef, useCallback } from 'react';
import { Upload, Camera, X, Loader2, Sparkles, Check, AlertCircle } from 'lucide-react';
import api from '../api';

interface OCRQuestion {
  question_number: string;
  question_content: string;
  options: string;
  student_answer: string;
  correct_answer: string;
  knowledge_point: string;
}

interface OCRResult {
  subject: string;
  exam_name: string;
  wrong_questions: OCRQuestion[];
  raw_text?: string;
  parse_error?: boolean;
}

interface OCRCaptureProps {
  onResultReady: (result: OCRResult) => void;
  disabled?: boolean;
}

export const OCRCapture: React.FC<OCRCaptureProps> = ({ onResultReady, disabled = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusText, setStatusText] = useState('等待上传...');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrResult, setOcrResult] = useState<OCRResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<File | null>(null);

  const processImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('请上传图片文件');
      return;
    }

    // 创建预览
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    fileRef.current = file;
    setIsProcessing(true);
    setStatusText('正在识别，请稍候...');
    setOcrResult(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/ai/ocr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 秒超时
      });

      if (response.data.success) {
        const result: OCRResult = {
          subject: response.data.subject || '',
          exam_name: response.data.exam_name || '',
          wrong_questions: response.data.wrong_questions || [],
          raw_text: response.data.raw_text,
          parse_error: response.data.parse_error,
        };
        setOcrResult(result);
        setStatusText('识别完成');
        onResultReady(result);
      } else {
        throw new Error(response.data.message || '识别失败');
      }
    } catch (error: any) {
      console.error('OCR 识别失败:', error);
      const errorMsg = error.response?.data?.message || error.message || '识别失败，请重试';
      setStatusText(`识别失败：${errorMsg}`);
      alert(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  }, [onResultReady]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) processImage(file);
  }, [disabled, processImage]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImage(file);
  }, [processImage]);

  const handleClear = useCallback(() => {
    setPreviewUrl(null);
    setOcrResult(null);
    setStatusText('等待上传...');
    fileRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleReRecognize = useCallback(() => {
    if (fileRef.current) {
      setOcrResult(null);
      processImage(fileRef.current);
    }
  }, [processImage]);

  return (
    <div className="space-y-3">
      {!previewUrl ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
          <div className="flex flex-col items-center gap-2">
            {isProcessing ? (
              <>
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                <p className="text-sm text-gray-600">{statusText}</p>
              </>
            ) : (
              <>
                <div className="flex gap-4">
                  <div className="flex flex-col items-center p-3 bg-gray-100 rounded-lg">
                    <Upload className="w-8 h-8 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500">上传图片</span>
                  </div>
                  <div className="flex flex-col items-center p-3 bg-gray-100 rounded-lg">
                    <Camera className="w-8 h-8 text-gray-500 mb-1" />
                    <span className="text-xs text-gray-500">拍照/截图</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  拖拽图片到这里，或点击选择文件
                </p>
                <p className="text-xs text-gray-400">
                  支持 JPG、PNG 等格式，AI 智能识别题目文字
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* 预览区域 */}
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full max-h-64 object-contain bg-gray-100"
            />
            {!isProcessing && (
              <button
                onClick={handleClear}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-white text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm">{statusText}</p>
                </div>
              </div>
            )}
          </div>

          {/* 识别结果 */}
          {ocrResult && (
            <div className="relative p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">识别结果</span>
                {ocrResult.parse_error && (
                  <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> 非结构化输出
                  </span>
                )}
              </div>

              {/* 科目和试卷名称 */}
              {ocrResult.subject && (
                <div className="mb-2 text-sm">
                  <span className="font-medium text-gray-600">科目：</span>
                  <span className="text-gray-800">{ocrResult.subject}</span>
                  {ocrResult.exam_name && (
                    <>
                      <span className="font-medium text-gray-600 ml-3">试卷：</span>
                      <span className="text-gray-800">{ocrResult.exam_name}</span>
                    </>
                  )}
                </div>
              )}

              {/* 错题列表 */}
              {ocrResult.wrong_questions.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {ocrResult.wrong_questions.map((q, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border border-gray-200 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {q.question_number || `第${idx + 1}题`}
                        </span>
                        {q.student_answer && (
                          <span className="text-xs text-red-600">
                            学生答案：{q.student_answer}
                          </span>
                        )}
                        {q.correct_answer && (
                          <span className="text-xs text-green-600">
                            正确答案：{q.correct_answer}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 line-clamp-2">{q.question_content}</p>
                      {q.knowledge_point && (
                        <p className="text-xs text-gray-500 mt-1">知识点：{q.knowledge_point}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : ocrResult.raw_text ? (
                <pre className="text-sm text-gray-700 whitespace-pre-wrap break-words font-sans max-h-48 overflow-y-auto">
                  {ocrResult.raw_text}
                </pre>
              ) : null}

              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleReRecognize}
                  className="text-xs px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                >
                  重新识别
                </button>
                {ocrResult.wrong_questions.length > 0 && (
                  <button
                    onClick={() => onResultReady(ocrResult)}
                    className="text-xs px-3 py-1 bg-green-500 text-white hover:bg-green-600 rounded transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    确认导入（{ocrResult.wrong_questions.length} 题）
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OCRCapture;
